import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const STEPS: { title: string; body: string; entity?: string; entityLabel?: string }[] = [
  {
    title: 'Start from published Modules',
    body: 'A Bundle composes Modules, so the Modules come first — each with its tiers, feature flags, and Variable Component refs already published. If a capability the new plan needs doesn\'t exist yet, publish that Module before touching the bundle.',
    entity: 'module',
    entityLabel: 'Module',
  },
  {
    title: 'Compose the Bundle as a draft',
    body: 'Catalog → Bundles → New. Pin the Module versions the plan includes, add any bundle-level feature flags (say, priority-support on Enterprise only), attach extra Variable Components that only this plan carries, and choose the billing periods it can be sold on. Everything stays in Draft — nothing is purchasable yet.',
    entity: 'bundle',
    entityLabel: 'Bundle',
  },
  {
    title: 'Ship a price in every currency you sell in',
    body: 'Set the bundle\'s base price, then make sure every Variable Component reachable from the bundle resolves to a price in every supported currency — a Global default is usually enough, with a bundle-scope override where this plan prices a component differently (setBundleComponentPrice, optionally per region and per billing term).',
    entity: 'variableComponentPrice',
    entityLabel: 'Variable Component Price',
  },
  {
    title: 'Run the publish-gap check',
    body: 'publishGapCheck renders a per-component, per-currency coverage matrix for the draft. isPublishable is true exactly when no row has a missing currency — a component with no resolvable EUR price will fail EUR subscriptions later, so this gate is not optional.',
  },
  {
    title: 'Publish',
    body: 'publishBundleDraftVersion validates the price chain and freezes the draft as an immutable new version. Existing subscriptions are untouched — they keep the version they pinned. The new version is what new subscriptions will pin from now on.',
    entity: 'bundle',
    entityLabel: 'Bundle',
  },
  {
    title: 'Sell it',
    body: 'Create a subscription against the new bundle (createSubscriptionWithPricing) — it pins the bundle version, freezes the base price in the customer\'s currency, and emits a CREATED lifecycle event. If existing customers should move onto the new plan, that\'s a migration — see the versioning guide.',
    entity: 'subscription',
    entityLabel: 'Subscription',
  },
];

export function LaunchBundleGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Launch a new Bundle — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Launch a new Bundle</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Launch a new <em>Bundle</em>.
        </h1>
        <p className="dp-oneliner">
          From draft composition to a purchasable, fully-priced plan — with the publish-gap check
          standing between you and a subscription that can&rsquo;t resolve a price.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link to={`/docs/concepts/${step.entity}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
